import * as React from 'react';
import { FC, memo, useCallback } from 'react';

import { makeUclComponent } from '@rbilabs/universal-components';

import { OnboardingStep } from 'components/onboarding-multi-step';
import { useOnboardingContext } from 'components/onboarding-multi-step/onboarding-context';
import { ONLY_SHOW_REDEEM_REWARD_IN_RESTAURANT_CTA } from 'pages/loyalty/loyalty-incentives-components/incentive-details/constants';
import { useInRestaurantRedemptionContext } from 'state/loyalty/in-restaurant-redemption';

import { RewardRedemptionItem } from './reward-redemption-item';
import { EmptyCartWidgetStyled, InRestaurantRedemptionList } from './reward-redemption.styled';
import { IRestaurantRedemptionItem, IRewardRedemptionListProps } from './types';

const RewardRedemptionList: FC<React.PropsWithChildren<IRewardRedemptionListProps>> = props => {
  const {
    inRestaurantRedemptionCart,
    removeInRestaurantRedemptionEntry,
    canRedeemLoyaltyIncentives,
  } = useInRestaurantRedemptionContext();
  const { isOnboardingAvailable, launchOnboarding } = useOnboardingContext();

  const handleOnboardingTrigger = useCallback(
    (link: string) => {
      if (isOnboardingAvailable) {
        launchOnboarding({ redirectLink: link });
      }
    },
    [isOnboardingAvailable, launchOnboarding]
  );

  if (!canRedeemLoyaltyIncentives) {
    return null;
  }

  if (!inRestaurantRedemptionCart.length) {
    const emptyCartWidget = props?.loyaltyEmptyCartTileWidget;

    if (emptyCartWidget) {
      const emptyCartWidgetLinkParams = ONLY_SHOW_REDEEM_REWARD_IN_RESTAURANT_CTA
        ? `?${ONLY_SHOW_REDEEM_REWARD_IN_RESTAURANT_CTA}=true`
        : '';
      const emptyCartWidgetLink = emptyCartWidget.link?.locale
        ? `${emptyCartWidget.link?.locale}${emptyCartWidgetLinkParams}`
        : '';
      return (
        <OnboardingStep
          targetId="empty_cart_widget"
          stepIndex="1"
          style={{ marginHorizontal: 16, marginVertical: 8 }}
        >
          <EmptyCartWidgetStyled
            title={emptyCartWidget.marketingBasicTileTitle?.locale ?? ''}
            description={emptyCartWidget.marketingBasicTileDescription?.localeRaw ?? ''}
            link={isOnboardingAvailable ? '' : emptyCartWidgetLink}
            linkLabel={emptyCartWidget.linkLabel?.locale ?? ''}
            onPress={() => handleOnboardingTrigger(emptyCartWidgetLink)}
          />
        </OnboardingStep>
      );
    }
  }

  const data = inRestaurantRedemptionCart?.map((cartEntry, i, arr) => ({
    id: cartEntry.referenceId,
    cartEntry,
    isLastChild: arr.length - 1 === i,
  }));

  if (!data?.length) {
    return null;
  }

  return (
    <OnboardingStep
      targetId="redemption_cart_list"
      stepIndex="1"
      style={{ alignItems: 'stretch', backgroundColor: 'transparent' }}
    >
      <InRestaurantRedemptionList {...props}>
        {data.map((item: IRestaurantRedemptionItem) => (
          <RewardRedemptionItem
            key={item.id}
            cartEntry={item.cartEntry}
            handleRemoveEntry={removeInRestaurantRedemptionEntry}
          />
        ))}
      </InRestaurantRedemptionList>
    </OnboardingStep>
  );
};

export default makeUclComponent(memo(RewardRedemptionList));
